/**
 * 路由预加载工具
 * 在用户悬停导航链接时提前加载对应页面组件
 */

// 页面模块映射
const pageModules: Record<string, () => Promise<unknown>> = {
  workbench: () => import('@/features/workbench/pages/workbench-page'),
  'recruitment-pipeline': () =>
    import('@/features/recruitment/pages/recruitment-pipeline'),
  'resume-management': () =>
    import('@/features/resume/pages/resume-management'),
  'job-profile': () => import('@/features/job/pages/job-profile'),
  'platform-config': () =>
    import('@/features/notification/pages/platform-config'),
  matching: () => import('@/features/matching/pages/intelligent-matching'),
  'intelligent-matching': () =>
    import('@/features/matching/pages/intelligent-matching'),
  'interview-management': () =>
    import('@/features/interview/pages/interview-management'),
  'offer-management': () => import('@/features/offer/pages/offer-management'),
  'system-settings': () =>
    import('@/features/system-settings/pages/system-settings'),
  'operation-log': () => import('@/features/audit/pages/audit-log'),
};

// 已预加载的模块缓存
const preloadedModules = new Set<string>();

/**
 * 预加载指定路由的页面组件
 * @param path 路由路径
 */
export function preloadRoute(path: string): void {
  // 移除开头的斜杠
  const cleanPath = path.replace(/^\//, '');

  // 如果已经预加载过，跳过
  if (preloadedModules.has(cleanPath)) {
    return;
  }

  // 查找对应的模块
  const loader = pageModules[cleanPath];
  if (loader) {
    preloadedModules.add(cleanPath);
    // 使用 requestIdleCallback 在空闲时加载
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loader());
    } else {
      // 降级方案
      setTimeout(() => loader(), 100);
    }
  }
}

/**
 * 预加载多个路由
 * @param paths 路由路径数组
 */
export function preloadRoutes(paths: string[]): void {
  paths.forEach(preloadRoute);
}

/**
 * 预加载所有主要路由（适合在应用启动后空闲时调用）
 */
export function preloadAllRoutes(): void {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        Object.keys(pageModules).forEach(preloadRoute);
      },
      { timeout: 5000 }
    );
  } else {
    setTimeout(() => {
      Object.keys(pageModules).forEach(preloadRoute);
    }, 3000);
  }
}

/**
 * React Hook: 在组件挂载后预加载所有路由
 */
export function usePreloadRoutes(): void {
  // 使用空依赖数组，确保只在挂载时执行一次
  if (typeof window !== 'undefined') {
    // 延迟 2 秒后开始预加载，避免影响首屏渲染
    setTimeout(preloadAllRoutes, 2000);
  }
}
